import styled from '@emotion/styled';
import { LinearProgress, linearProgressClasses, LinearProgressProps } from '@mui/material';
import React, { FormEventHandler } from 'react';

const AnimatedProgressBar = ({
	label,
	value,
	delay = 1000,
	showLabel = true,
	sliderProps,
	onChange,
	min = 0,
	max = 1000,
	intervalTime = 1,
	showMaxValue = true,
	pathBgColor,
	barBgColor,
}: {
	label: React.ReactElement;
	value: number;
	delay?: number;
	showLabel?: boolean;
	sliderProps?: LinearProgressProps;
	onChange?: FormEventHandler<HTMLSpanElement>;
	min?: number;
	max?: number;
	intervalTime?: number;
	showMaxValue?: boolean;
	pathBgColor?: string;
	barBgColor?: string;
}) => {
	const [progress, setProgress] = React.useState(0);

	const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
		height: 10,
		borderRadius: '4px',
		[`&.${linearProgressClasses.colorPrimary}`]: {
			backgroundColor: pathBgColor ?? 'rgb(238, 238, 238)',
		},
		[`& .${linearProgressClasses.bar}`]: {
			borderRadius: '4px',
			backgroundColor: barBgColor ?? '#6698FA',
		},
	}));

	React.useEffect(() => {
		const animateProgress = async () => {
			await new Promise(resolve => setTimeout(resolve, delay));

			const interval = setInterval(() => {
				setProgress(prevProgress => {
					const nextProgress = prevProgress + 1;
					if (nextProgress >= value) {
						clearInterval(interval);
					}
					return nextProgress;
				});
			}, intervalTime); // Adjust the interval based on your preference for animation speed

			await new Promise(resolve => setTimeout(resolve, value * intervalTime)); // Wait for the animation to finish

			clearInterval(interval);
		};

		animateProgress();
	}, [value, delay]);

	const normalise = (value: number) => ((value - min) * 100) / (max - min);

	return (
		<div>
			{showLabel ? (
				<div className="flex justify-between mb-[5px] text-sm text-[#1F2A37] font-[500]">
					<p>{label}</p>
					<p>
						{progress}
						{showMaxValue ? (
							<>
								/<span className="text-[10px]">{max ? max : 100}</span>
							</>
						) : (
							''
						)}
					</p>
				</div>
			) : null}
			<BorderLinearProgress variant="determinate" value={normalise(progress)} onChange={onChange} {...sliderProps} />
		</div>
	);
};

export default AnimatedProgressBar;
